// dashboardPortfolioDiversification.tsx

import { useCallback, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { FooterButton } from '@/components/common/FooterButton';
import AssetAllocationPieChart from '@/modules/portfolio/charts/assetAllocationPieChart';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { useLocalization } from '@/hooks/useLocalization';
import { useNavigate } from 'react-router';
import AsOfDatePicker from '@/components/date/AsOfDatePicker';
import dayjs from '@/config/dayjs';
import { useCanNavigateTo } from '@/hooks/useCanNavigateTo';
import { DASHBOARD_WIDGET_DATE_PICKER_SX } from '@/constants/dashboardWidgetDatePickerSx';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { dashboardMobileWidthValue } from '@/constants';
import { paths } from '@/constants/paths';
import { ContentContainer } from '../contentContainer';
import { ExportButton } from '@/components/grids/ExportButton';
import { ExportTableRequest } from '@/store/exportApi';
import { useEffectiveExclusions } from '@/hooks/useEffectiveExclusions';

type DashboardPortfolioDiversificationProps = StandardWidgetProps;

const isEmbeddableBundle = import.meta.env.VITE_WIDGET === 'external';

export default function DashboardPortfolioDiversification({
  onDataLoaded,
  title,
  isWidget = false,
  isHalfWidth = false,
  container = 'widget',
}: DashboardPortfolioDiversificationProps) {
  const { t } = useLocalization();
  const widgetTitle = title ?? t('grid.assetAllocation');
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(
    dayjs().format('MM/DD/YYYY'),
  ); //datepicker starts with today
  const [isChartUpdating, setIsChartUpdating] = useState(true);
  const effectiveExclusions = useEffectiveExclusions();
  // Institution exclusions are expanded into account IDs asynchronously. Until
  // that finishes (or if it fails) the export params would be missing them and
  // the download would include holdings from excluded institutions.
  const exportBlockedByExclusions =
    effectiveExclusions.isResolvingExclusions ||
    effectiveExclusions.exclusionResolutionFailed;

  const handleChartUpdatingChange = useCallback((updating: boolean) => {
    setIsChartUpdating(updating);
  }, []);

  const disableDatePicker = isEmbeddableBundle && isChartUpdating;
  const canViewDiversification = useCanNavigateTo(
    paths.investor.portfolio.assetAllocation,
  );
  const handleSelectDate = (selectedDate: string) => {
    const dateToValidate = dayjs(selectedDate);
    const today = dayjs(); // current moment
    if (dateToValidate.isAfter(today, 'day')) {
      console.error('Future dates are not allowed');
      return;
    }
    setSelectedDate(selectedDate);
  };

  // Force the widget into the XS/mobile layout earlier than `sm` (600px).
  // This keeps half-width (and narrow full-width) dashboard tiles matching the intended mobile design.
  const { ref: widgetWidthRef, isBelow: isWidgetBelowMobileWidth } =
    useWidgetWidthIsBelow(isWidget, dashboardMobileWidthValue);

  // For dashboard widgets, "half width" should always behave like the XS/mobile layout.
  // Additionally, if the widget container itself is narrow, force XS layout even if viewport is wide.
  const forceMobile =
    (isWidget && isHalfWidth) || (isWidget && isWidgetBelowMobileWidth);

  const portfolioLabel = t('Asset Allocation').replace('Portfolio', '');

  // Legacy implementation: https://github.com/Wealth-Access/legacy-ui/blob/main/Scripts/src/User/Components/PortfolioDiversification/Chart.jsx#L143
  const exportConfig: ExportTableRequest = {
    filename: 'Diversification',
    dataUrl: 'diversificationHoldings',
    params: {
      asOfDate: selectedDate,
      diversificationId: 1,
      categoryId: 0,
      sort: [
        {
          Property: 'y',
          Direction: 'desc',
        },
      ],
      ...(effectiveExclusions.excludedAccounts.length > 0
        ? { ExcludedAccounts: effectiveExclusions.excludedAccounts }
        : {}),
      ...(effectiveExclusions.excludedAccountTypes.length > 0
        ? { ExcludedAccountTypes: effectiveExclusions.excludedAccountTypes }
        : {}),
      ...(effectiveExclusions.excludedCategories.length > 0
        ? { ExcludedCategories: effectiveExclusions.excludedCategories }
        : {}),
      ...(effectiveExclusions.excludedInstitutions.length > 0
        ? { ExcludedInstitutions: effectiveExclusions.excludedInstitutions }
        : {}),
    },
    columns: [
      {
        Property: 'DiversificationName',
        Label: portfolioLabel,
      },
      {
        Label: 'Holding',
        Property: 'name',
        Type: 'string',
      },
      {
        Label: 'Account',
        Property: 'AccountName',
        Type: 'string',
      },
      {
        Label: 'Institution',
        Property: 'InstitutionName',
        Type: 'string',
      },
      {
        Label: `Percent of holding that is ${portfolioLabel} type`,
        Property: 'PercentOfHolding',
      },
      {
        Label: `Balance of ${portfolioLabel} type`,
        Property: 'y',
        Type: 'currency',
      },
      {
        Label: 'Entire Holding Balance',
        Property: 'TotalHoldingValue',
        Type: 'number',
      },
      {
        Label: 'Quantity',
        Property: 'Quantity',
        Type: 'number',
      },
      {
        Label: 'Price',
        Property: 'Price',
        Type: 'currency',
      },
    ],
  };

  return (
    <ContentContainer
      container={container}
      sx={{ textAlign: 'center' }}
      ref={widgetWidthRef}
    >
      <Box>
        {/* Title & date range selector */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'top-center',
            mb: 2,
          }}
        >
          <Typography variant='h5'>{widgetTitle}</Typography>
          <Stack
            flexDirection='row'
            gap={1}
          >
            <ExportButton
              columns={exportConfig.columns}
              fileName={exportConfig.filename}
              dataUrl={exportConfig.dataUrl}
              queryParams={exportConfig.params}
              disabled={exportBlockedByExclusions}
              isIconOnly={forceMobile}
            />

            <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
              <AsOfDatePicker
                date={selectedDate}
                onChange={handleSelectDate}
                label={t('assetAllocation.asOf')}
                useWidgetNarrowIconField={isWidget && isWidgetBelowMobileWidth}
                disabled={disableDatePicker}
              />
            </Box>
          </Stack>
        </Box>

        {/* View mode toggle: chart, pie, bar */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            // This row is intentionally empty per Jira; don't force a scrollbar.
            width: '100%',
          }}
        ></Box>

        <AssetAllocationPieChart
          asOfDate={new Date(selectedDate)}
          // For Pie, we always show the start & end comparison
          // so no displayMode toggle needed for the data
          onDataLoaded={onDataLoaded}
          onChartUpdatingChange={handleChartUpdatingChange}
          forceMobile={forceMobile}
          isWidget={isWidget}
        />
      </Box>
      {canViewDiversification && !isEmbeddableBundle && (
        <FooterButton
          buttonText={t('viewFullAllocationDetails')}
          onClick={() => {
            void navigate(paths.investor.portfolio.assetAllocation);
          }}
        />
      )}
    </ContentContainer>
  );
}
